import { Component, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UserManagementService } from 'src/app/services/user-management.service';
import { ToastService } from 'src/app/services/toast.service';

type PasswordErrors = { current_password?: string; new_password?: string };

// Mirrors the backend rule in pulsewatch/api/v1/auth/password_util.py.
const PASSWORD_MIN = 5;
const PASSWORD_MAX = 24;

function samePasswords(group: AbstractControl): ValidationErrors | null {
  const { new_password, confirm_password } = group.value;
  return new_password === confirm_password ? null : { mismatch: true };
}

function firstError(value: unknown): string | undefined {
  return Array.isArray(value) ? String(value[0]) : typeof value === 'string' ? value : undefined;
}

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  standalone: false
})
export class AccountPageComponent implements OnInit {

  profileForm: FormGroup;
  orgForm: FormGroup;
  passwordForm: FormGroup;
  isSavingProfile = signal(false);
  isSavingOrg = signal(false);
  isSavingPassword = signal(false);
  passwordErrors = signal<PasswordErrors>({});
  readonly passwordRule = `${PASSWORD_MIN}–${PASSWORD_MAX} characters, with at least one letter and one number.`;

  constructor(
    private fb: FormBuilder,
    private userService: UserManagementService,
    private toast: ToastService,
  ) {}

  async ngOnInit() {
    this.profileForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      phone: [''],
    });

    this.orgForm = this.fb.group({
      company_name: [''],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      address: [''],
      zip: [''],
      location: [''],
      country: [''],
    });

    this.passwordForm = this.fb.group({
      current_password: ['', Validators.required],
      new_password: ['', [
        Validators.required,
        Validators.minLength(PASSWORD_MIN),
        Validators.maxLength(PASSWORD_MAX),
        Validators.pattern(/^(?=.*[a-zA-Z])(?=.*[0-9]).*$/),
      ]],
      confirm_password: ['', Validators.required],
    }, { validators: samePasswords });

    // A server-side error belongs to the value it was reported for.
    this.passwordForm.valueChanges.subscribe(() => {
      if (Object.keys(this.passwordErrors()).length) this.passwordErrors.set({});
    });

    this.userService.getMe().subscribe(user => {
      this.profileForm.patchValue({
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone,
      });
    });

    this.userService.getOrganization().subscribe(org => {
      this.orgForm.patchValue(org);
    });
  }

  isInvalid(form: FormGroup, field: string): boolean {
    const ctrl = form.get(field);
    return ctrl.invalid && ctrl.touched;
  }

  saveProfile() {
    this.profileForm.markAllAsTouched();
    if (this.profileForm.invalid) return;
    this.isSavingProfile.set(true);
    this.userService.updateMe(this.profileForm.value).subscribe({
      next: () => {
        this.toast.show('Profile updated successfully.', 'success');
        this.isSavingProfile.set(false);
      },
      error: () => {
        this.toast.show('Failed to update profile.', 'error');
        this.isSavingProfile.set(false);
      }
    });
  }

  saveOrganization() {
    this.orgForm.markAllAsTouched();
    if (this.orgForm.invalid) return;
    this.isSavingOrg.set(true);
    this.userService.updateOrganization(this.orgForm.value).subscribe({
      next: () => {
        this.toast.show('Organization updated successfully.', 'success');
        this.isSavingOrg.set(false);
      },
      error: () => {
        this.toast.show('Failed to update organization.', 'error');
        this.isSavingOrg.set(false);
      }
    });
  }

  confirmMismatch(): boolean {
    const confirm = this.passwordForm.get('confirm_password');
    return confirm.touched && (confirm.invalid || this.passwordForm.hasError('mismatch'));
  }

  changePassword() {
    this.passwordForm.markAllAsTouched();
    if (this.passwordForm.invalid) return;
    const { current_password, new_password } = this.passwordForm.value;
    this.isSavingPassword.set(true);
    this.userService.changePassword(current_password, new_password).subscribe({
      next: () => {
        this.isSavingPassword.set(false);
        this.passwordForm.reset();
        this.toast.show('Password changed successfully.', 'success');
      },
      error: (body) => {
        this.isSavingPassword.set(false);
        const errors: PasswordErrors = {
          current_password: firstError(body?.current_password),
          new_password: firstError(body?.new_password),
        };
        this.passwordErrors.set(errors);
        if (!errors.current_password && !errors.new_password) {
          this.toast.show('Failed to change password.', 'error');
        }
      }
    });
  }
}
